import { DiscrepancyPopup } from "@/components/discrepancies/data-discrepancies/DiscrepancyPopup";
import { Form } from "@/components/shared/forms/Form";
import {
  CurrentView,
  EditDialogState,
  useDiscrepanciesPage,
} from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";
import { CHLDAddEditForm } from "@/components/records/child/CHLDAddEditForm";
import Card from "@/components/shared/Card";
import CategoryFilterCell from "@/components/shared/tables/CategoryFilterCell";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { recordTypes } from "@/models";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiLink,
  FiLock,
  FiSave,
} from "react-icons/fi";
import Popup from "@/components/shared/Popup";
import { UseFormReturn } from "react-hook-form";

const SORTED_RECORD_TYPES = [...recordTypes].sort();

const columns: GridColumnProps[] = [
  {
    id: "RecordID",
    field: "RecordID",
    title: "Record ID",
  },
  {
    id: "ReferenceRecordType",
    field: "ReferenceRecordType",
    title: "Record Type",
    cells: {
      filterCell: (props) => CategoryFilterCell(props, SORTED_RECORD_TYPES),
    },
  },
  { id: "ErrorCode", field: "ErrorCode", title: "Error Code" },
  { id: "ErrorMessage", field: "ErrorMessage", title: "Error Message" },
];

function SSIDNoAccessEmptyState() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 px-4">
      <div
        className="d-flex align-items-center justify-content-center mb-3"
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#F1F5F9",
          color: "#64748B",
        }}
      >
        <FiLock size={26} />
      </div>
      <h5 className="mb-2" style={{ color: "#1E3A5F", fontWeight: 600 }}>
        No access to SSID Sync
      </h5>
      <p className="mb-0" style={{ color: "#64748B", maxWidth: 420 }}>
        Contact your administrator to request access.
      </p>
    </div>
  );
}

export function SSIDSyncTab() {
  const {
    currentView,
    selectedDiscrepancy,
    editDialog,
    coreData,
    tableData,
    error,
    forms,
    returnToTab,
    isSubmitting,
    anomalyRun,
    handleRowClick,
    handleCloseAll,
    handleUpdateRecord,
    handleBackToDiscrepancy,
    handleSave,
    getRecordLabel,
  } = useDiscrepanciesPage();

  const { canViewRecord, canEditRecord } = useRecordPermissions();
  const canEditCHLD = canEditRecord("CHLD");

  const ssidTableData = tableData.filter((row) =>
    row.ErrorCode?.startsWith("SSID")
  );

  const tableKey = ssidTableData.map((row) => row.RecordID).join(",");

  if (!canViewRecord("CHLD")) {
    return <SSIDNoAccessEmptyState />;
  }

  return (
    <>
      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            background: "var(--color-p3)",
            fontWeight: 500,
            borderRadius: "0.375rem 0.375rem 0px 0px",
          }}
        >
          <FiLink size={18} />
          <span>SSID Errors By Record</span>
        </div>
        <KendoTableClientState
          key={tableKey}
          data={ssidTableData}
          columns={columns}
          error={error}
          onRowClick={canEditCHLD ? handleRowClick : undefined}
        />
      </Card>

      {canEditCHLD && currentView === "discrepancy" && selectedDiscrepancy && (
        <DiscrepancyPopup
          key={`discrepancy-popup-${returnToTab}`}
          discrepancy={selectedDiscrepancy}
          onClose={handleCloseAll}
          errorMessageMap={coreData.errorMessageMap}
          onUpdateRecord={handleUpdateRecord}
          initialTab={returnToTab}
          anomalyRunEndTime={anomalyRun?.EndTime}
          title="SSID Sync"
          titleIcon={<FiLink size={20} />}
        />
      )}

      {/* CHLD Edit Dialog — Update CHLD required to launch; view-only users cannot reach this dialog because row click is disabled */}
      <EditDialog
        type="CHLD"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.chld}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditCHLD}
      >
        <CHLDAddEditForm
          selectedChild={
            editDialog?.type === "CHLD" ? editDialog.data : undefined
          }
          form={forms.chld}
        />
      </EditDialog>
    </>
  );
}

type EditDialogProps = {
  type: string;
  editDialog: EditDialogState;
  currentView: CurrentView;
  form: UseFormReturn<any>;
  onClose: () => void;
  onBack: () => void;
  onSave: (data: any) => void;
  getRecordLabel: (type: string) => string;
  isSubmitting: boolean;
  canEdit: boolean;
  children: React.ReactNode;
};

function EditDialog({
  type,
  editDialog,
  currentView,
  form,
  onClose,
  onBack,
  onSave,
  getRecordLabel,
  isSubmitting,
  canEdit,
  children,
}: EditDialogProps) {
  if (currentView !== "edit" || !editDialog || editDialog.type !== type) {
    return null;
  }

  const formId = `${type.toLowerCase()}-edit-form`;

  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
          <span>
            {canEdit ? "Edit" : "View"} {getRecordLabel(type)} Record
          </span>
        </div>
      }
      size="lg"
      onClose={onClose}
      hasButton={false}
      submitFormId={formId}
      submitButtonText={
        <div className="d-flex align-items-center gap-2">
          <FiSave size={16} />
          <span>Save</span>
        </div>
      }
      submitButtonLoading={isSubmitting}
      submitButtonLoadingText="Saving..."
      secondButtonText="Cancel"
      onSecondButtonClick={onClose}
      backButtonText={
        <div className="d-flex align-items-center gap-2">
          <FiArrowLeft size={16} />
          <span>Back</span>
        </div>
      }
      onBackButtonClick={onBack}
      canEdit={canEdit}
    >
      <Form id={formId} onSubmit={form.handleSubmit(onSave)}>
        {children}
      </Form>
    </Popup>
  );
}
